import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockIcon from "@mui/icons-material/Lock";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";

import type {
  ProviderId,
  SetWorkloadProviderRequest,
  WorkloadGroupRow,
  WorkloadRoutingStatus,
  WorkloadRow,
} from "../types.js";
import { modelDisplayName } from "@/lib/modelLabel";

import { providerLabel } from "./format.js";

/**
 * Per-feature provider routing — the table under the global switch.
 *
 * The operator asked to pick a provider per feature rather than run
 * "several providers at once" with no visible logic. Two levels: set a
 * GROUP once and every feature under it inherits, then override the
 * individual ones that need something different.
 *
 * Three things this has to communicate, because getting them wrong is what
 * made the old surface confusing:
 *
 *  1. **What will actually run.** Every row shows its EFFECTIVE provider,
 *     not just its override — a blank row that silently inherits is how
 *     you end up not knowing what your own system does.
 *  2. **Where that came from.** "Inherited from Harvester" vs an explicit
 *     pin, so a value is never unexplained.
 *  3. **Why a choice is unavailable.** Tool-calling and streaming are
 *     Anthropic-only in today's gateway, so those rows are locked with the
 *     reason shown rather than mysteriously refusing to change.
 */
export function WorkloadRoutingCard({
  routing,
  onChange,
  onFollowGroup,
  busyKey,
  error,
}: {
  routing: WorkloadRoutingStatus;
  onChange: (body: SetWorkloadProviderRequest) => void;
  /** Clear every feature override in a group so all follow the group. */
  onFollowGroup?: (group: WorkloadGroupRow) => void;
  busyKey: string | null;
  error: string | null;
}): JSX.Element {
  // Groups start EXPANDED (2026-09-25): collapsed, the per-feature pickers
  // were invisible, and a group row reading "Anthropic" over thirteen
  // features individually pinned to DeepSeek looked like the page said one
  // thing while the agent did another.
  const [open, setOpen] = useState<Record<string, boolean>>({});

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        What each feature runs on
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Set a provider for a whole system, then override individual features
        that need something else. Anything left alone follows its group, and a
        group left alone follows the global default above
        {" ("}
        {providerLabel(routing.global_provider)}
        {")."}
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {routing.unregistered.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {routing.unregistered.length} feature(s) call the AI but are not
          registered, so they cannot be steered here and run on the global
          default: {routing.unregistered.join(", ")}
        </Alert>
      ) : null}

      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        {routing.groups.map((group) => (
          <GroupBlock
            key={group.key}
            group={group}
            routing={routing}
            expanded={open[group.key] ?? true}
            onToggle={() =>
              setOpen((prev) => ({ ...prev, [group.key]: !(prev[group.key] ?? true) }))
            }
            onChange={onChange}
            {...(onFollowGroup ? { onFollowGroup: () => onFollowGroup(group) } : {})}
            busyKey={busyKey}
          />
        ))}
      </Box>
    </Box>
  );
}

function GroupBlock({
  group,
  routing,
  expanded,
  onToggle,
  onChange,
  onFollowGroup,
  busyKey,
}: {
  group: WorkloadGroupRow;
  routing: WorkloadRoutingStatus;
  expanded: boolean;
  onToggle: () => void;
  onChange: (body: SetWorkloadProviderRequest) => void;
  onFollowGroup?: () => void;
  busyKey: string | null;
}): JSX.Element {
  const overridden = group.workloads.filter(
    (w) => w.override_provider || w.override_model,
  ).length;
  const runsOn = runsOnSummary(group);

  return (
    <Box sx={{ "&:not(:last-of-type)": { borderBottom: 1, borderColor: "divider" } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          bgcolor: "surfaceSoft",
        }}
      >
        <IconButton
          size="small"
          onClick={onToggle}
          aria-label={`${expanded ? "Collapse" : "Expand"} ${group.label}`}
          aria-expanded={expanded}
          sx={{
            transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 150ms",
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>{group.label}</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {group.description}
          </Typography>
          <Typography
            variant="caption"
            component="div"
            sx={{ color: "text.primary", fontWeight: 500 }}
            data-testid={`group-runs-on-${group.key}`}
          >
            Runs on: {runsOn}
            {overridden > 0 ? (
              <Box component="span" sx={{ color: "warning.main", fontWeight: 400 }}>
                {" "}· {overridden} feature{overridden > 1 ? "s" : ""} set separately
              </Box>
            ) : null}
          </Typography>
        </Box>

        {overridden > 0 && onFollowGroup ? (
          <Button
            size="small"
            variant="outlined"
            onClick={onFollowGroup}
            disabled={busyKey === `group:${group.key}`}
          >
            Make all {overridden} follow this group
          </Button>
        ) : null}

        <ProviderPicker
          value={group.override_provider}
          allowed={group.allowed_providers}
          inheritLabel={`Global (${providerLabel(routing.global_provider)})`}
          busy={busyKey === `group:${group.key}`}
          ariaLabel={`Provider for ${group.label}`}
          onPick={(provider) =>
            onChange({ scope: "group", key: group.key, provider, model: null })
          }
        />
        <ModelPicker
          value={group.override_model}
          provider={group.override_provider ?? routing.global_provider}
          models={routing.models_by_provider}
          busy={busyKey === `group:${group.key}`}
          ariaLabel={`Model for ${group.label}`}
          onPick={(model) =>
            onChange({
              scope: "group",
              key: group.key,
              provider: group.override_provider,
              model,
            })
          }
        />
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 2, pb: 1.5 }}>
          {group.workloads.map((w) => (
            <WorkloadRowItem
              key={w.key}
              row={w}
              routing={routing}
              groupLabel={group.label}
              busy={busyKey === `workload:${w.key}`}
              onChange={onChange}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

/** What a group's features ACTUALLY run on — "DeepSeek ×13", or
 * "Anthropic ×3 · DeepSeek ×2" when they differ. The group's own picker
 * shows only its setting, which features pinned separately ignore. */
function runsOnSummary(group: WorkloadGroupRow): string {
  const counts = new Map<string, number>();
  for (const w of group.workloads) {
    const label = w.effective_model
      ? `${providerLabel(w.effective_provider)} (${modelDisplayName(w.effective_model)})`
      : providerLabel(w.effective_provider);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label, n]) => (counts.size === 1 && n === 1 ? label : `${label} ×${n}`))
    .join(" · ");
}

function WorkloadRowItem({
  row,
  routing,
  groupLabel,
  busy,
  onChange,
}: {
  row: WorkloadRow;
  routing: WorkloadRoutingStatus;
  groupLabel: string;
  busy: boolean;
  onChange: (body: SetWorkloadProviderRequest) => void;
}): JSX.Element {
  const locked = row.allowed_providers.length <= 1;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1,
        pl: 5,
        "&:not(:last-of-type)": { borderBottom: 1, borderColor: "divider" },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {row.label}
          </Typography>
          {locked ? (
            <Tooltip
              title={`Only Anthropic implements ${row.requires.join(" and ")} today, so this feature cannot move.`}
            >
              <LockIcon
                fontSize="inherit"
                aria-label="locked"
                sx={{ color: "text.disabled" }}
              />
            </Tooltip>
          ) : null}
          <SourceChip row={row} groupLabel={groupLabel} />
        </Box>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {row.description}
        </Typography>
      </Box>

      <ProviderPicker
        value={row.override_provider}
        allowed={row.allowed_providers}
        inheritLabel={`Inherit (${providerLabel(row.effective_provider)})`}
        disabled={locked}
        busy={busy}
        ariaLabel={`Provider for ${row.label}`}
        onPick={(provider) =>
          onChange({ scope: "workload", key: row.key, provider, model: null })
        }
      />
      <ModelPicker
        value={row.override_model}
        provider={row.effective_provider}
        models={routing.models_by_provider}
        busy={busy}
        ariaLabel={`Model for ${row.label}`}
        onPick={(model) =>
          onChange({
            scope: "workload",
            key: row.key,
            provider: row.override_provider,
            model,
          })
        }
      />
    </Box>
  );
}

/**
 * Where this row's provider came from. A value with no explanation is what
 * made the old surface untrustworthy — an operator should never have to
 * guess why a feature says what it says.
 */
function SourceChip({
  row,
  groupLabel,
}: {
  row: WorkloadRow;
  groupLabel: string;
}): JSX.Element | null {
  if (row.source === "workload") {
    return <Chip size="small" label="Pinned" color="primary" variant="outlined" />;
  }
  const from = row.source === "group" ? groupLabel : "global default";
  return (
    <Chip
      size="small"
      variant="outlined"
      label={`${providerLabel(row.effective_provider)} · from ${from}`}
      sx={{ color: "text.secondary" }}
    />
  );
}

function ProviderPicker({
  value,
  allowed,
  inheritLabel,
  onPick,
  busy,
  disabled,
  ariaLabel,
}: {
  value: ProviderId | null;
  allowed: string[];
  inheritLabel: string;
  onPick: (provider: ProviderId | null) => void;
  busy: boolean;
  disabled?: boolean | undefined;
  ariaLabel: string;
}): JSX.Element {
  return (
    <Select
      size="small"
      value={value ?? ""}
      // Coerced: `exactOptionalPropertyTypes` won't let an explicit
      // `undefined` reach MUI's optional `disabled`.
      disabled={Boolean(busy || disabled)}
      displayEmpty
      inputProps={{ "aria-label": ariaLabel }}
      onChange={(e) => {
        const next = e.target.value;
        onPick(next === "" ? null : (next as ProviderId));
      }}
      sx={{ minWidth: 168 }}
    >
      {/* Clearing back to inherited is a real choice, not a delete. */}
      <MenuItem value="">
        <em>{inheritLabel}</em>
      </MenuItem>
      {allowed.map((p) => (
        <MenuItem key={p} value={p}>
          {providerLabel(p)}
        </MenuItem>
      ))}
    </Select>
  );
}

function ModelPicker({
  value,
  provider,
  models,
  onPick,
  busy,
  ariaLabel,
}: {
  value: string | null;
  provider: string;
  models: Record<string, string[]>;
  onPick: (model: string | null) => void;
  busy: boolean;
  ariaLabel: string;
}): JSX.Element {
  // Offered straight from the gateway's live model list for the resolved
  // provider, so this dropdown cannot drift from what the gateway accepts.
  const options = models[provider] ?? [];
  return (
    <Select
      size="small"
      value={value && options.includes(value) ? value : ""}
      disabled={busy || options.length === 0}
      displayEmpty
      inputProps={{ "aria-label": ariaLabel }}
      onChange={(e) => {
        const next = e.target.value;
        onPick(next === "" ? null : String(next));
      }}
      sx={{ minWidth: 180 }}
    >
      <MenuItem value="">
        <em>Provider default</em>
      </MenuItem>
      {options.map((m) => (
        <MenuItem key={m} value={m}>
          {modelDisplayName(m)}
        </MenuItem>
      ))}
    </Select>
  );
}
